import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';
import PostCard from 'components/PostCard';

import Button from 'primitives/Button';
import EmptyState from 'primitives/EmptyState';
import LoadingIndicator from 'primitives/LoadingIndicator';

import PostSlideOver from './PostSlideOver';

const POSTS_QUERY = gql(`
  query PostsQuery($cursor: ID, $limit: Int) {
    dashboard {
      id
      posts(cursor: $cursor, limit: $limit) {
        nodes {
          id
          title
          status
          message
          createdAt
          publishedAt
          deal {
            id
            companyInformation {
              company {
                name
              }
            }
          }
          sender {
            id  
            name
          }
          syndicate {
            id
            name
            image
          }
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
    }
  }
`);

const Posts: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [postOnDisplay, setPostOnDisplay] = useState(() => searchParams.get('postId') ?? '');

  const openPost = (id: string) => {
    setPostOnDisplay(id);
    setSearchParams(id ? { postId: id } : {}, { replace: true });
  };

  const closePost = () => {
    setPostOnDisplay('');
    setSearchParams({}, { replace: true });
  };

  const { loading, error, data, refetch, fetchMore } = useQuery(POSTS_QUERY, {
    variables: {
      limit: 20,
    },
  });

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data || !data.dashboard.posts)
      return <ErrorMessage error={error} refetch={refetch} />;

    const posts = data.dashboard.posts.nodes;
    const pageInfo = data.dashboard.posts.pageInfo;

    if (posts.length === 0)
      return (
        <EmptyState
          title="No posts received"
          description="Updates and information from Syndicates will appear here"
        />
      );

    return (
      <>
        <PostSlideOver open={!!postOnDisplay} postId={postOnDisplay} onClose={closePost} />
        <div className="flex flex-col">
          <div className="space-y-2 overflow-auto">
            {posts.map(post => (
              <PostCard
                key={post.id}
                id={post.id}
                title={post.title}
                message={post.message}
                createdAt={post.createdAt}
                deal={post.deal}
                sender={post.sender}
                syndicate={post.syndicate}
                publishedAt={post.publishedAt}
                onClick={() => openPost(post.id)}
              />
            ))}
          </div>
          {pageInfo.hasNextPage ? (
            <div className="flex justify-center p-4">
              <Button
                loading={loading}
                onClick={() =>
                  fetchMore({
                    variables: {
                      cursor: pageInfo.cursor,
                    },
                  })
                }
              >
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return <>{renderContent()}</>;
};

export default Posts;
